import React, { useState, useRef, useEffect, useCallback } from 'react';
import JakeMessage from '../../components/JakeMessage';
import api from '../../services/api';
import './JakeOrderModal.css';

const JakeOrderModal = ({ isOpen, onClose, onOrdersCreated }) => {
  const [mode, setMode] = useState('ai'); // 'ai' or 'manual'

  // AI mode state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [ordersCreated, setOrdersCreated] = useState(false);

  // Manual mode state
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_po_number: '',
    item_name: '',
    item_color: '',
    item_sku: '',
    item_size: '',
    quantity: 1,
    price: '',
    payment_status: 'Not Paid',
    salesperson_id: '',
    notes: '',
  });
  const [users, setUsers] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when modal opens or mode changes
  useEffect(() => {
    if (isOpen && mode === 'ai' && !isStreaming) {
      inputRef.current?.focus();
    }
  }, [isOpen, isStreaming, mode]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('ai');
      setMessages([]);
      setInput('');
      setOrdersCreated(false);
      setIsStreaming(false);
      setFormData({
        customer_name: '',
        customer_phone: '',
        customer_po_number: '',
        item_name: '',
        item_color: '',
        item_sku: '',
        item_size: '',
        quantity: 1,
        price: '',
        payment_status: 'Not Paid',
        salesperson_id: '',
        notes: '',
      });
      setFormError(null);
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/special-orders/users/active');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  // Build conversation history for API
  const buildHistory = useCallback(() => {
    return messages.map(m => ({
      role: m.role,
      content: m.content
    }));
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const history = buildHistory();
    setInput('');
    setIsStreaming(true);

    const assistantId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    }]);

    try {
      const response = await fetch('/api/jake/special-order-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage.content,
          history: history
        })
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'text') {
                fullContent += data.content;
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, content: fullContent } : m
                ));
              } else if (data.type === 'orders_created') {
                setOrdersCreated(true);
                if (onOrdersCreated) {
                  onOrdersCreated(data.orders);
                }
              } else if (data.type === 'done') {
                if (ordersCreated) {
                  setTimeout(() => onClose(), 2000);
                }
              }
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Sorry, something went wrong. Please try again.' }
          : m
      ));
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, buildHistory, ordersCreated, onClose, onOrdersCreated]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    if (!isStreaming && !formLoading) {
      onClose();
    }
  };

  // Manual form handlers
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'customer_phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const response = await api.post('/special-orders/orders', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        if (onOrdersCreated) {
          onOrdersCreated([{ id: response.data.orderId }]);
        }
        onClose();
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setFormLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="jake-order-modal-overlay" onClick={handleClose}>
      <div className={`jake-order-modal ${mode === 'manual' ? 'jake-order-modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header with Tabs */}
        <div className="jake-order-modal-header">
          <div className="jake-order-modal-tabs">
            <button
              className={`jake-order-tab ${mode === 'ai' ? 'active' : ''}`}
              onClick={() => setMode('ai')}
            >
              <span className="jake-order-tab-icon">J</span>
              Ask Jake
            </button>
            <button
              className={`jake-order-tab ${mode === 'manual' ? 'active' : ''}`}
              onClick={() => setMode('manual')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
              Manual Entry
            </button>
          </div>
          <button
            className="jake-order-modal-close"
            onClick={handleClose}
            disabled={isStreaming || formLoading}
            title="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {mode === 'ai' ? (
          <>
            {/* AI Chat Messages */}
            <div className="jake-order-modal-messages">
              {messages.length === 0 ? (
                <div className="jake-order-modal-welcome">
                  <p>Tell me about the special order(s) you need to create.</p>
                  <p className="jake-order-modal-hint">
                    Include customer name, phone, item, SKU, size, color, and price.
                  </p>
                  <div className="jake-order-modal-examples">
                    <button onClick={() => setInput('Special order for ')}>
                      Start with customer...
                    </button>
                    <button onClick={() => setInput('I have 2 special orders: ')}>
                      Multiple orders...
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map(message => (
                    <div key={message.id} className={`jake-order-message jake-order-message-${message.role}`}>
                      {message.role === 'assistant' && (
                        <div className="jake-order-message-avatar">J</div>
                      )}
                      <div className="jake-order-message-content">
                        {message.role === 'assistant' ? (
                          <JakeMessage content={message.content} />
                        ) : (
                          <div className="jake-order-message-text">{message.content}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isStreaming && messages[messages.length - 1]?.content === '' && (
                    <div className="jake-order-message jake-order-message-assistant">
                      <div className="jake-order-message-avatar">J</div>
                      <div className="jake-order-message-content">
                        <div className="jake-order-typing">
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* AI Chat Input */}
            <div className="jake-order-modal-input">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={ordersCreated ? "Orders created! Closing..." : "Describe the special order(s)..."}
                rows={2}
                disabled={isStreaming || ordersCreated}
              />
              <button
                className="jake-order-send-btn"
                onClick={sendMessage}
                disabled={!input.trim() || isStreaming || ordersCreated}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          /* Manual Form */
          <form className="jake-order-manual-form" onSubmit={handleFormSubmit}>
            {formError && <div className="jake-order-form-error">{formError}</div>}

            <div className="jake-order-form-grid">
              <div className="jake-order-form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Phone *</label>
                <input
                  type="text"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleFormChange}
                  placeholder="830-555-1234"
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>PO Number *</label>
                <input
                  type="text"
                  name="customer_po_number"
                  value={formData.customer_po_number}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  name="item_name"
                  value={formData.item_name}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>SKU *</label>
                <input
                  type="text"
                  name="item_sku"
                  value={formData.item_sku}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Size *</label>
                <input
                  type="text"
                  name="item_size"
                  value={formData.item_size}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Color *</label>
                <input
                  type="text"
                  name="item_color"
                  value={formData.item_color}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Price *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleFormChange}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleFormChange}
                  min="1"
                />
              </div>

              <div className="jake-order-form-group">
                <label>Payment Status</label>
                <select
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleFormChange}
                >
                  <option value="Not Paid">Not Paid</option>
                  <option value="Paid in Full">Paid in Full</option>
                </select>
              </div>

              <div className="jake-order-form-group">
                <label>Salesperson</label>
                <select
                  name="salesperson_id"
                  value={formData.salesperson_id}
                  onChange={handleFormChange}
                >
                  <option value="">Select...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="jake-order-form-group jake-order-form-full">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  rows={2}
                />
              </div>
            </div>

            <div className="jake-order-form-actions">
              <button type="button" onClick={handleClose} disabled={formLoading}>
                Cancel
              </button>
              <button type="submit" className="jake-order-form-submit" disabled={formLoading}>
                {formLoading ? 'Creating...' : 'Create & Print'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default JakeOrderModal;
